import type { Flight, LogbookEntry } from '@/lib/db';
import {
  aircraft as defaultAircraftRepo,
  flights as defaultFlightRepo,
  logbookEntries as defaultLogbookRepo,
} from '@/lib/db';
import type { AircraftRepository, FlightRepository, LogbookRepository } from '@/lib/db';

import type { ParsedLogbookRow } from './types';

/**
 * Turns parsed interchange rows into draft `LogbookEntry` rows — the shared
 * second half every format module (`native.ts`, `foreflight.ts`,
 * `myflightbook.ts`) hands off to, so dedup and draft-and-approve are written
 * once rather than once per format.
 *
 * Draft-and-approve, same as an imported track (`draft.ts`): nothing an
 * external file says counts toward totals until a pilot has looked at it
 * (`NZ-LOGBOOK.md` §2.3), so every imported row lands with `isDraft: true`.
 *
 * Dedup only has the one case `draft.ts` calls "a hand-written entry already
 * covers it" — an interchange row carries no track to compare flight times
 * against, so any existing entry on the same aircraft and calendar day counts
 * as a match outright. This makes re-importing the same file, or two
 * exports that overlap, a no-op rather than a pile of duplicate drafts; two
 * genuinely different flights in one aircraft on one day still only get the
 * first one drafted, the same trade-off `draft.ts` makes for a hand-written
 * entry with no track on it.
 */

export interface CommitDeps {
  aircraftRepo?: AircraftRepository;
  flightRepo?: FlightRepository;
  logbookRepo?: LogbookRepository;
  now?: () => string;
}

export interface CommitSummary {
  imported: number;
  /** Already had a logbook entry on the same aircraft and day. */
  skipped: number;
}

export async function commitImportedRows(
  rows: readonly ParsedLogbookRow[],
  deps: CommitDeps = {},
): Promise<CommitSummary> {
  const aircraftRepo = deps.aircraftRepo ?? defaultAircraftRepo;
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const logbookRepo = deps.logbookRepo ?? defaultLogbookRepo;
  const now = deps.now ?? (() => new Date().toISOString());

  let imported = 0;
  let skipped = 0;

  for (const row of rows) {
    const aircraft =
      row.registration === undefined
        ? undefined
        : await aircraftRepo.findByRegistration(row.registration);

    const candidates =
      aircraft === undefined
        ? (await logbookRepo.listOrdered()).filter(
            (entry) => entry.date === row.date && entry.registration === row.registration,
          )
        : await logbookRepo.listByAircraftAndDate(aircraft.id, row.date);

    if (candidates.length > 0) {
      skipped += 1;
      continue;
    }

    const timestamp = now();
    const flight: Flight = {
      id: crypto.randomUUID(),
      date: row.date,
      ...(aircraft === undefined ? {} : { aircraftId: aircraft.id }),
      derived: { confidence: 'low' },
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    await flightRepo.put(flight);

    const entry: LogbookEntry = {
      id: crypto.randomUUID(),
      flightId: flight.id,
      date: row.date,
      ...(aircraft === undefined ? {} : { aircraftId: aircraft.id }),
      ...(row.registration === undefined ? {} : { registration: row.registration }),
      ...(row.aircraftType === undefined ? {} : { aircraftType: row.aircraftType }),
      pic: row.pic ?? 'SELF',
      ...(row.from === undefined ? {} : { from: row.from }),
      ...(row.to === undefined ? {} : { to: row.to }),
      ...(row.intermediate === undefined ? {} : { intermediate: row.intermediate }),
      ...(row.remarks === undefined ? {} : { remarks: row.remarks }),
      time: row.time,
      approaches: row.approaches,
      landings: row.landings,
      takeoffs: row.takeoffs,
      isDraft: true,
      derivedFields: [],
      history: [],
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    await logbookRepo.put(entry);
    imported += 1;
  }

  return { imported, skipped };
}
